import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle, CheckCircle2, XCircle, Clock, DollarSign, AlertCircle } from "lucide-react";
import { runsApi } from "../../lib/workflows";

const STATUS_META = {
  queued:        { color: "#94A3B8", label: "Queued" },
  running:       { color: "#3B82F6", label: "Running" },
  waiting_human: { color: "#F59E0B", label: "Waiting" },
  completed:     { color: "#10B981", label: "Completed" },
  failed:        { color: "#EF4444", label: "Failed" },
  cancelled:     { color: "#64748B", label: "Cancelled" },
};

export default function Runs() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  useEffect(() => {
    setLoading(true);
    runsApi.list({ status: status || undefined })
      .then((r) => setRuns(r.runs || []))
      .finally(() => setLoading(false));
  }, [status]);

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="runs-page">
      <div className="flex items-center gap-2 mb-1">
        <PlayCircle size={20} strokeWidth={2.5} />
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Runs</div>
      </div>
      <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">Run history</h1>
      <p className="text-slate-600 mb-6 max-w-2xl">Every workflow execution AiMedley has ever performed for you.</p>

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="status-filters">
        {["", "running", "waiting_human", "completed", "failed"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            className={`text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${
              status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
            }`}
            data-testid={`filter-${s || "all"}`}
          >
            {s ? STATUS_META[s]?.label || s : "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 italic">Loading…</div>
      ) : (
        <div data-testid="runs-list" className="space-y-2">
          {runs.length === 0
            ? <div className="brut-card p-8 text-center text-slate-500">
                No runs yet. Head to <Link to="/workforce/workflows" className="font-heading font-bold text-slate-900 border-b-2 border-slate-900 hover:border-amber-500">Workflows</Link> and run one.
              </div>
            : runs.map((r) => <RunRow key={r.run_id} r={r} />)}
        </div>
      )}
    </div>
  );
}

function RunRow({ r }) {
  const meta = STATUS_META[r.status] || { color: "#64748B", label: r.status };
  const duration = r.started_at && r.completed_at
    ? Math.round((new Date(r.completed_at) - new Date(r.started_at)) / 1000)
    : null;
  return (
    <Link to={`/workforce/runs/${r.run_id}`} className="brut-card p-4 flex items-center gap-4 hover:-translate-y-0.5 transition-transform block" data-testid={`run-${r.run_id}`}>
      <div className="w-11 h-11 rounded-xl border-2 border-slate-900 flex items-center justify-center shrink-0"
           style={{ backgroundColor: meta.color + "22", color: meta.color }}>
        {r.status === "running" ? <PlayCircle size={18} strokeWidth={2.5} /> :
         r.status === "completed" ? <CheckCircle2 size={18} strokeWidth={2.5} /> :
         r.status === "failed" ? <XCircle size={18} strokeWidth={2.5} /> :
         r.status === "waiting_human" ? <AlertCircle size={18} strokeWidth={2.5} /> :
         <Clock size={18} strokeWidth={2.5} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <div className="font-heading font-bold text-base truncate">{r.workflow_name || "(deleted)"}</div>
          <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full border-2"
                style={{ borderColor: meta.color, color: meta.color, backgroundColor: meta.color + "1F" }}>
            {meta.label}
          </span>
        </div>
        <div className="text-xs text-slate-500 font-mono truncate">#{r.run_id}</div>
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-500 font-mono shrink-0">
        {duration !== null && <span>{duration}s</span>}
        <span>${(r.total_cost || 0).toFixed(4)}</span>
        <span>{r.started_at ? relDate(r.started_at) : ""}</span>
      </div>
    </Link>
  );
}

function relDate(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}
